import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, X, Dumbbell, Heart, Activity } from 'lucide-react';
import { Exercise } from '../../../types/exercises';
import { exerciseLibrary } from '../../../data/exerciseLibrary';

interface ExerciseLibraryProps {
  onClose: () => void;
  onSelect: (exercise: Exercise) => void;
}

export function ExerciseLibrary({ onClose, onSelect }: ExerciseLibraryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedMuscle, setSelectedMuscle] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'All', icon: <Activity className="w-4 h-4" /> },
    { id: 'strength', name: 'Strength', icon: <Dumbbell className="w-4 h-4" /> },
    { id: 'cardio', name: 'Cardio', icon: <Heart className="w-4 h-4" /> }
  ];

  const filteredExercises = exerciseLibrary.filter(exercise => {
    const matchesSearch = exercise.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || exercise.category === selectedCategory;
    const matchesMuscle = selectedMuscle === 'all' || exercise.primaryMuscles.includes(selectedMuscle);
    return matchesSearch && matchesCategory && matchesMuscle;
  });

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-xl border border-border w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">Exercise Library</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-4 border-b border-border space-y-4">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search exercises..."
              className="w-full bg-muted rounded-lg pl-10 pr-4 py-2 text-sm"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2 theme-scrollbar">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70'
                }`}
              >
                {category.icon}
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Exercise List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filteredExercises.map((exercise) => (
            <motion.button
              key={exercise.id}
              onClick={() => onSelect(exercise)}
              className="w-full p-4 bg-muted/30 rounded-lg flex items-center justify-between hover:bg-muted/50 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-4">
                {exercise.image ? (
                  <img
                    src={exercise.image}
                    alt={exercise.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Dumbbell className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div className="text-left">
                  <div className="font-medium">{exercise.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {exercise.primaryMuscles.join(', ')}
                  </div>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                {exercise.difficulty}
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}